<template>
  <AuthLayout>
    <!-- Step 1: 입력 폼 -->
    <div v-if="step === 1">
      <div class="trial-head">
        <div class="trial-badge">무료체험 신청</div>
        <h1 class="trial-title">Free trial</h1>
      </div>

      <form class="trial-form" @submit.prevent="submitTrial">
        <UiInput
          placeholder="이름"
          type="text"
          v-model="form.name"
          autocomplete="name"
        />

        <UiInput
          placeholder="휴대폰 번호 ( - 제외)"
          type="tel"
          inputmode="tel"
          v-model="form.phone"
          autocomplete="tel"
        />

        <!-- 날짜 선택 -->
        <div class="form-group">
          <div
            class="custom-date-input"
            :class="{
              disabled: scheduleLoading || availableDates.length === 0,
            }"
            role="button"
            :aria-disabled="scheduleLoading || availableDates.length === 0"
            tabindex="0"
            @click="openCalendar"
            @keydown.enter="openCalendar"
            @keydown.space.prevent="openCalendar"
          >
            <span v-if="!form.preferredDays" class="date-placeholder">
              {{ datePlaceholder }}
            </span>
            <span v-else class="date-value">
              {{ formatDate(form.preferredDays) }}
            </span>
            <svg
              class="calendar-icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <rect
                x="3"
                y="4"
                width="14"
                height="13"
                rx="2"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path d="M3 8H17" stroke="currentColor" stroke-width="1.5" />
              <path
                d="M7 2V5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M13 2V5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div v-if="dateError" class="error-message">
            {{ dateError }}
          </div>
          <button
            v-if="scheduleError"
            type="button"
            class="schedule-retry"
            @click="loadFreeTrialClasses"
          >
            수업 일정 다시 불러오기
          </button>
        </div>

        <!-- 커스텀 달력 모달 -->
        <div
          v-if="showCalendar"
          class="calendar-overlay"
          @click="showCalendar = false"
        >
          <div class="calendar-modal" @click.stop>
            <div class="calendar-header">
              <button type="button" class="calendar-nav" @click="prevMonth">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <div class="calendar-title">
                {{ currentYear }}년 {{ currentMonth }}월
              </div>
              <button type="button" class="calendar-nav" @click="nextMonth">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>

            <div class="calendar-weekdays">
              <div class="weekday">일</div>
              <div class="weekday">월</div>
              <div class="weekday">화</div>
              <div class="weekday">수</div>
              <div class="weekday">목</div>
              <div class="weekday">금</div>
              <div class="weekday">토</div>
            </div>

            <div class="calendar-days">
              <button
                type="button"
                v-for="day in calendarDays"
                :key="day.key"
                class="calendar-day"
                :class="{
                  'other-month': !day.isCurrentMonth,
                  disabled: day.isDisabled,
                  selected: day.isSelected,
                  today: day.isToday,
                }"
                :disabled="day.isDisabled"
                @click="selectDate(day)"
              >
                {{ day.date }}
              </button>
            </div>
          </div>
        </div>

        <!-- 시간 선택 -->
        <div class="form-group">
          <select
            v-model="form.preferredTimes"
            class="form-time-select"
            :disabled="!form.preferredDays || availableTimes.length === 0"
          >
            <option value="">체험 희망 시간</option>
            <option
              v-for="time in availableTimes"
              :key="time.value"
              :value="time.value"
            >
              {{ time.label }}
            </option>
          </select>
        </div>

        <!-- 개인정보 처리방침 동의 (필수) -->
        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="form.agreeToPrivacy"
              class="checkbox-input"
              required
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              <a
                href="#"
                @click.prevent="openTermsModal('privacy')"
                class="terms-link"
              >
                개인정보 처리방침
              </a>
              에 동의합니다 <span class="required">(필수)</span>
            </span>
          </label>
        </div>

        <!-- 마케팅 정보 수신동의 (선택) -->
        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="form.agreeToMarketing"
              class="checkbox-input"
            />
            <span class="checkbox-custom"></span>
            <span class="checkbox-text">
              <a
                href="#"
                @click.prevent="openTermsModal('marketing')"
                class="terms-link"
              >
                마케팅 정보 수신
              </a>
              에 동의합니다 <span class="optional">(선택)</span>
            </span>
          </label>
        </div>

        <UiButton
          type="submit"
          :disabled="!canSubmit || loading"
          :loading="loading"
        >
          신청하기
        </UiButton>
      </form>
    </div>

    <!-- Step 2: 성공 화면 -->
    <div v-else class="success-content">
      <div class="success-icon">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="50" fill="#2A4624" />
          <circle cx="50" cy="50" r="36" fill="#78FF47" />
          <path
            d="M35 50 L45 60 L65 40"
            stroke="#000000"
            stroke-width="5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="success-title">신청이 완료되었습니다!</h2>
      <p class="success-message">
        예약이 확정되면 문자로 안내드리며,<br />
        추가 확인이 필요하면 담당자가<br />
        <span class="phone-highlight">{{ formatPhone(form.phone) }}</span
        >(으)로 연락드리겠습니다.
      </p>

      <div class="success-actions">
        <UiButton @click="goToSignIn">확인</UiButton>
      </div>
    </div>

    <!-- 약관 모달 -->
    <div v-if="showTermsModal" class="modal-overlay" @click="closeTermsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ modalTitle }}</h3>
          <button class="modal-close" @click="closeTermsModal">×</button>
        </div>
        <div class="modal-body">
          <div v-html="modalContent" class="terms-content"></div>
        </div>
        <div class="modal-footer">
          <UiButton @click="closeTermsModal">확인</UiButton>
        </div>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AuthLayout from "@/components/AuthLayout.vue";
import { api, apiJson } from "@/lib/api";

export default {
  name: "FreeTrialView",
  components: { UiButton, UiInput, AuthLayout },
  data() {
    return {
      step: 1,
      loading: false,
      dateError: "",
      showTermsModal: false,
      showCalendar: false,
      modalTitle: "",
      modalContent: "",
      calendarYear: new Date().getFullYear(),
      calendarMonth: new Date().getMonth(),
      scheduleLoading: true,
      scheduleError: "",
      freeTrialClasses: [],
      form: {
        name: "",
        phone: "",
        preferredDays: "",
        preferredTimes: "",
        agreeToPrivacy: false,
        agreeToMarketing: false,
      },
    };
  },
  computed: {
    phoneValid() {
      const digits = (this.form.phone || "").replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 11;
    },
    canSubmit() {
      const { name, phone, preferredDays, preferredTimes, agreeToPrivacy } =
        this.form;
      return (
        name &&
        phone &&
        preferredDays &&
        preferredTimes &&
        this.availableTimes.some((time) => time.value === preferredTimes) &&
        agreeToPrivacy &&
        this.phoneValid
      );
    },
    currentYear() {
      return this.calendarYear;
    },
    currentMonth() {
      return this.calendarMonth + 1;
    },
    availableDates() {
      return [
        ...new Set(
          this.freeTrialClasses
            .map((classItem) => classItem.date)
            .filter(Boolean)
        ),
      ].sort();
    },
    datePlaceholder() {
      if (this.scheduleLoading) return "수업 일정을 불러오는 중입니다";
      if (this.scheduleError) return "수업 일정을 불러오지 못했습니다";
      if (this.availableDates.length === 0)
        return "신청 가능한 수업이 없습니다";
      return "체험 희망 날짜";
    },
    calendarDays() {
      const days = [];
      const firstDay = new Date(this.calendarYear, this.calendarMonth, 1);
      const lastDay = new Date(this.calendarYear, this.calendarMonth + 1, 0);
      const prevLastDay = new Date(this.calendarYear, this.calendarMonth, 0);

      const firstDayOfWeek = firstDay.getDay();
      const lastDate = lastDay.getDate();
      const prevLastDate = prevLastDay.getDate();

      const seoulToday = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());

      // 이전 달 날짜
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = prevLastDate - i;
        days.push({
          date,
          isCurrentMonth: false,
          isDisabled: true,
          isSelected: false,
          isToday: false,
          key: `prev-${date}`,
        });
      }

      // 현재 달 날짜
      for (let date = 1; date <= lastDate; date++) {
        const dateStr = `${this.calendarYear}-${String(
          this.calendarMonth + 1
        ).padStart(2, "0")}-${String(date).padStart(2, "0")}`;

        days.push({
          date,
          dateStr,
          isCurrentMonth: true,
          isDisabled: !this.availableDates.includes(dateStr),
          isSelected: this.form.preferredDays === dateStr,
          isToday: dateStr === seoulToday,
          key: `current-${date}`,
        });
      }

      // 다음 달 날짜
      const remainingDays = 42 - days.length;
      for (let date = 1; date <= remainingDays; date++) {
        days.push({
          date,
          isCurrentMonth: false,
          isDisabled: true,
          isSelected: false,
          isToday: false,
          key: `next-${date}`,
        });
      }

      return days;
    },
    availableTimes() {
      if (!this.form.preferredDays) return [];
      const times = this.freeTrialClasses
        .filter((classItem) => classItem.date === this.form.preferredDays)
        .map((classItem) => classItem.start_time)
        .filter(Boolean);

      return [...new Set(times)]
        .sort()
        .map((time) => ({ value: time, label: time }));
    },
  },
  mounted() {
    this.loadFreeTrialClasses();
  },
  methods: {
    formatDate(dateStr) {
      const [year, month, day] = dateStr.split("-").map(Number);
      return `${year}년 ${month}월 ${day}일`;
    },
    async loadFreeTrialClasses() {
      this.scheduleLoading = true;
      this.scheduleError = "";

      try {
        const data = await apiJson("/v1/free-trial/classes");
        this.freeTrialClasses = Array.isArray(data?.classes)
          ? data.classes
          : [];

        if (
          this.form.preferredDays &&
          !this.availableDates.includes(this.form.preferredDays)
        ) {
          this.form.preferredDays = "";
          this.form.preferredTimes = "";
        }

        if (
          this.form.preferredTimes &&
          !this.availableTimes.some(
            (time) => time.value === this.form.preferredTimes
          )
        ) {
          this.form.preferredTimes = "";
        }

        const firstDate = this.availableDates[0];
        if (firstDate) {
          const [year, month] = firstDate.split("-").map(Number);
          this.calendarYear = year;
          this.calendarMonth = month - 1;
        }
      } catch (error) {
        console.error("무료체험 수업 일정 로드 실패:", error);
        this.freeTrialClasses = [];
        this.scheduleError = "수업 일정을 불러오지 못했습니다.";
      } finally {
        this.scheduleLoading = false;
      }
    },
    openCalendar() {
      if (this.scheduleLoading || this.availableDates.length === 0) return;
      this.showCalendar = true;
    },
    selectDate(day) {
      if (day.isDisabled || !day.isCurrentMonth) return;

      this.form.preferredDays = day.dateStr;
      this.dateError = "";
      this.form.preferredTimes = "";
      this.showCalendar = false;
    },
    prevMonth() {
      if (this.calendarMonth === 0) {
        this.calendarMonth = 11;
        this.calendarYear--;
      } else {
        this.calendarMonth--;
      }
    },
    nextMonth() {
      if (this.calendarMonth === 11) {
        this.calendarMonth = 0;
        this.calendarYear++;
      } else {
        this.calendarMonth++;
      }
    },
    async submitTrial() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;

      try {
        await api("/v1/free-trial", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: this.form.name,
            phone_number: this.form.phone.replace(/\D/g, ""),
            preferred_days: this.form.preferredDays,
            preferred_times: this.form.preferredTimes,
            agree_to_privacy: this.form.agreeToPrivacy,
            agree_to_marketing: this.form.agreeToMarketing,
          }),
        });

        this.step = 2;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push("/signin");
    },
    formatPhone(phone) {
      return phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    },
    async openTermsModal(type) {
      this.modalTitle =
        type === "privacy" ? "개인정보 처리방침" : "마케팅 정보 수신동의";

      try {
        const response = await fetch(`/terms/${type}_2025-10-22.md`);
        if (response.ok) {
          const text = await response.text();
          this.modalContent = this.convertMarkdownToHtml(text);
        } else {
          this.modalContent = "<p>약관 내용을 불러올 수 없습니다.</p>";
        }
      } catch (error) {
        console.error("약관 로드 실패:", error);
        this.modalContent = "<p>약관 내용을 불러올 수 없습니다.</p>";
      }

      this.showTermsModal = true;
    },
    closeTermsModal() {
      this.showTermsModal = false;
      this.modalContent = "";
    },
    convertMarkdownToHtml(markdown) {
      return markdown
        .replace(/^# (.*$)/gim, "<h1>$1</h1>")
        .replace(/^## (.*$)/gim, "<h2>$1</h2>")
        .replace(/^### (.*$)/gim, "<h3>$1</h3>")
        .replace(/\*\*(.*)\*\*/gim, "<strong>$1</strong>")
        .replace(/\*(.*)\*/gim, "<em>$1</em>")
        .replace(/\n\n/gim, "</p><p>")
        .replace(/\n/gim, "<br>")
        .replace(/^(.*)$/gim, "<p>$1</p>")
        .replace(/<p><\/p>/gim, "");
    },
  },
};
</script>

<style scoped>
/* 제목 */
.trial-head {
  margin-bottom: 24px;
  text-align: left;
}

.trial-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.trial-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.trial-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

.trial-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
  font-size: 14px;
  font-family: "Pretendard", sans-serif;
}

.trial-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}

.trial-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

/* 폼 그룹 */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 커스텀 날짜 입력 */
.custom-date-input {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff;
  background: #151718;
  color: #fff;
  padding: 0 14px;
  font-size: 14px;
  font-family: "Pretendard", sans-serif;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-date-input:hover {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
}

.custom-date-input.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.custom-date-input.disabled:hover {
  box-shadow: none;
}

.date-placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.date-value {
  color: #fff;
}

.calendar-icon {
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

/* 달력 모달 */
.calendar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.calendar-modal {
  background: #1a1c1e;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.calendar-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.calendar-nav {
  background: none;
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.calendar-nav:hover {
  background: rgba(255, 255, 255, 0.1);
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  padding: 8px 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  aspect-ratio: 1;
  border: none;
  background: none;
  color: #fff;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Pretendard", sans-serif;
}

.calendar-day:hover:not(.disabled) {
  background: rgba(120, 255, 71, 0.15);
}

.calendar-day.other-month {
  color: rgba(255, 255, 255, 0.3);
}

.calendar-day.disabled {
  color: rgba(255, 255, 255, 0.3);
  cursor: not-allowed;
  text-decoration: line-through;
}

.calendar-day.selected {
  background: #78ff47;
  color: #000;
  font-weight: 600;
}

.calendar-day.today {
  border: 2px solid #78ff47;
}

/* 시간 선택 */
.form-time-select {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff;
  background: #151718;
  color: rgba(255, 255, 255, 0.4);
  padding: 0 14px;
  font-size: 14px;
  font-family: "Pretendard", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
}

.form-time-select:valid {
  color: #fff;
}

.form-time-select:focus {
  outline: none;
  border-color: #fff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.form-time-select:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.form-time-select option {
  background: #151718;
  color: #fff;
  padding: 8px;
  font-size: 14px;
}

.form-time-select option:first-child {
  color: rgba(255, 255, 255, 0.4);
}

.error-message {
  color: #ff4647;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 500;
}

.schedule-retry {
  align-self: flex-start;
  border: 0;
  background: transparent;
  color: #78ff47;
  padding: 0;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
}

/* 체크박스 */
.checkbox-group {
  margin-top: 8px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.9);
}

.checkbox-input {
  display: none;
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 2px solid #fff;
  border-radius: 4px;
  background: #151718;
  position: relative;
  flex-shrink: 0;
  margin-top: 1px;
  transition: all 0.3s ease;
}

.checkbox-input:checked + .checkbox-custom {
  background: #78ff47;
  border-color: #78ff47;
}

.checkbox-input:checked + .checkbox-custom::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #000;
  font-weight: bold;
  font-size: 12px;
}

.checkbox-text {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.terms-link {
  color: #78ff47;
  text-decoration: underline;
  text-decoration-color: rgba(120, 255, 71, 0.5);
  transition: all 0.3s ease;
}

.terms-link:hover {
  color: #a8ff71;
  text-decoration-color: #78ff47;
}

.required {
  color: #ff4647;
  font-weight: 500;
}

.optional {
  color: rgba(255, 255, 255, 0.6);
}

.trial-form .ui-btn {
  width: 100%;
}

/* 약관 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #151718;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #fff;
}

.modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.terms-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 14px;
}

.terms-content h1 {
  color: #78ff47;
  font-size: 20px;
  margin: 0 0 16px 0;
  font-weight: 600;
}

.terms-content h2 {
  color: #78ff47;
  font-size: 18px;
  margin: 24px 0 12px 0;
  font-weight: 600;
}

.terms-content h3 {
  color: #fff;
  font-size: 16px;
  margin: 20px 0 8px 0;
  font-weight: 500;
}

.terms-content p {
  margin: 0 0 12px 0;
}

.terms-content strong {
  color: #78ff47;
  font-weight: 600;
}

.terms-content em {
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
}

.modal-footer {
  padding: 24px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
}

.modal-footer .ui-btn {
  min-width: 120px;
}

/* 성공 화면 */
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.success-icon {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.success-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -2px;
  color: #ffffff;
  text-align: center;
}

.success-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.8);
}

.success-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.success-actions .ui-btn {
  width: 100%;
}

.phone-highlight {
  color: #78ff47;
  font-weight: 600;
}

@media (max-width: 640px) {
  .trial-title,
  .success-title {
    font-size: 32px;
  }

  .modal-content {
    max-width: 95%;
    max-height: 90vh;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 20px;
  }

  .modal-title {
    font-size: 20px;
  }

  .terms-content {
    font-size: 13px;
  }

  .calendar-modal {
    max-width: 340px;
  }
}
</style>
